// Keybindings: the one remappable home for every desktop flight control.
//
// The air game was flown by hardcoded key literals scattered through input.js
// ("KeyW" here, `["KeyS","ArrowDown"]` there). That's fine until a pilot flies
// left-handed, or on AZERTY, or just wants throttle on the mouse wheel hand —
// then there was nowhere to change it. This module hoists all of those literals
// into ONE table of actions, each with its default physical-key codes, and lets
// the player override them from Settings > Keybindings. input.js reads the live
// keymap every frame, so a rebind takes hold the instant it's saved.
//
// Everything keys off KeyboardEvent.code (the PHYSICAL key, layout-independent)
// exactly as input.js always did — so a binding recorded on QWERTY still lands
// on the same key of an AZERTY board. The on-screen labels ("W", "↑", "Shift")
// are cosmetic; the code underneath is what's stored.
//
// SCOPE: these are the AIRCRAFT flight controls (pitch/roll/yaw/throttle, guns,
// ordnance, and the pilot toggles). The naval/land conning keys and the
// spectator camera keys live in main.js, are gated by mode, and are deliberately
// left out of this table — remapping those would need per-context binding sets,
// a bigger job than the one the pilot actually asked for.

export const KEYBIND_STORAGE = "va-keybindings";

// The canonical action table — the single source of truth. Order (and group)
// here is the order rendered in the Settings screen. `defaults` is the physical
// key code list each action ships with; multiple codes mean "any of these" (the
// letter AND the arrow, both Shifts, etc.). `hold` marks the two actions whose
// key is held rather than tapped (the scoreboard peek and the comms wheel) — the
// editor labels them so a player knows the feel before they rebind.
export const ACTIONS = [
  // ——— Flight ———
  { id: "pitchUp", label: "Pitch up / climb", group: "Flight", defaults: ["KeyW", "ArrowUp"] },
  { id: "pitchDown", label: "Pitch down / dive", group: "Flight", defaults: ["KeyS", "ArrowDown"] },
  { id: "rollLeft", label: "Roll left", group: "Flight", defaults: ["KeyA", "ArrowLeft"] },
  { id: "rollRight", label: "Roll right", group: "Flight", defaults: ["KeyD", "ArrowRight"] },
  { id: "yawLeft", label: "Rudder left", group: "Flight", defaults: ["KeyQ"] },
  { id: "yawRight", label: "Rudder right", group: "Flight", defaults: ["KeyE"] },
  // ——— Throttle ———
  { id: "throttleUp", label: "Throttle up", group: "Throttle", defaults: ["ShiftLeft", "ShiftRight", "KeyZ"] },
  { id: "throttleDown", label: "Throttle down", group: "Throttle", defaults: ["ControlLeft", "ControlRight", "KeyX"] },
  // ——— Weapons ———
  { id: "fire", label: "Fire guns", group: "Weapons", defaults: ["Space"] },
  { id: "bomb", label: "Drop bomb / torpedo / rockets", group: "Weapons", defaults: ["KeyB"] },
  // Jets era only (the actions exist everywhere; the era config decides
  // whether the plane has anything on the rails or in the rack).
  { id: "missile", label: "Launch heat-seeker (jets era)", group: "Weapons", defaults: ["KeyR"] },
  { id: "flare", label: "Punch flares (jets era)", group: "Weapons", defaults: ["KeyL"] },
  // ——— Systems ———
  { id: "autopilot", label: "Autopilot (straight & level)", group: "Systems", defaults: ["KeyP"] },
  { id: "autoTrim", label: "Auto-trim (hold altitude)", group: "Systems", defaults: ["KeyT"] },
  { id: "mouseAim", label: "Toggle mouse aim", group: "Systems", defaults: ["KeyV"] },
  { id: "invertPitch", label: "Toggle inverted pitch", group: "Systems", defaults: ["KeyI"] },
  { id: "spot", label: "Spot / mark target", group: "Systems", defaults: ["KeyG"] },
  { id: "bail", label: "Bail out", group: "Systems", defaults: ["KeyF"] },
  { id: "comms", label: "Comms wheel", group: "Systems", defaults: ["KeyH"], hold: true },
  { id: "scoreboard", label: "Scoreboard", group: "Systems", defaults: ["Tab"], hold: true },
  { id: "mute", label: "Mute audio", group: "Systems", defaults: ["KeyM"] },
  { id: "settings", label: "Settings & keybindings", group: "Systems", defaults: ["KeyO"] },
];

// Group order for the Settings screen (any group not listed falls to the end).
export const GROUPS = ["Flight", "Throttle", "Weapons", "Systems"];

const ACTION_BY_ID = new Map(ACTIONS.map((a) => [a.id, a]));

function defaultsFor(id) {
  return (ACTION_BY_ID.get(id)?.defaults ?? []).slice();
}

// Codes whose browser default we suppress (scroll, tab-focus, Firefox quick-find,
// back-navigation) — but ONLY while they're actually bound to a control, so an
// unbound key still behaves like a normal key on the page. input.js consults
// this every keydown instead of the old hardcoded Space/Arrows/Shift/Ctrl list,
// which went stale the moment you rebound throttle onto PageUp.
const HIJACK_CODES = new Set([
  "Space", "Tab",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
  "ShiftLeft", "ShiftRight", "ControlLeft", "ControlRight", "AltLeft", "AltRight",
  "PageUp", "PageDown", "Home", "End", "Backspace", "Slash", "Quote",
]);

class KeyMap {
  constructor() {
    this.map = {}; // actionId -> [code, ...]
    this._byCode = new Map(); // code -> Set(actionId) — rebuilt on every change
    this._listeners = new Set();
    this.load();
  }

  // Start from the shipped defaults, then overlay any saved overrides. Merging
  // over defaults (rather than trusting the blob whole) means a new action added
  // to the table in a later build still gets its default for a returning player
  // whose saved map predates it — the same zero-means-default spirit the Go
  // config readers use.
  load() {
    const map = {};
    for (const a of ACTIONS) map[a.id] = a.defaults.slice();
    try {
      const saved = JSON.parse(localStorage.getItem(KEYBIND_STORAGE) ?? "{}");
      for (const a of ACTIONS) {
        const codes = saved?.[a.id];
        if (Array.isArray(codes)) {
          const clean = codes.filter((c) => typeof c === "string" && c.length > 0);
          map[a.id] = [...new Set(clean)];
        }
      }
    } catch {
      // Corrupt blob — fall back to the defaults already in `map`.
    }
    // Enforce the one-key-one-action invariant even against a stale or
    // hand-edited blob (or one an older build's reset() left with a cross-action
    // duplicate): if a code appears under two actions, keep it on the first in
    // table order and strip it from the rest. Without this, a saved map with the
    // same key under two ids would fire both on one press.
    const claimed = new Set();
    for (const a of ACTIONS) {
      map[a.id] = map[a.id].filter((c) => (claimed.has(c) ? false : (claimed.add(c), true)));
    }
    this.map = map;
    this._reindex();
  }

  _reindex() {
    this._byCode = new Map();
    for (const id of Object.keys(this.map)) {
      for (const code of this.map[id]) {
        let set = this._byCode.get(code);
        if (!set) this._byCode.set(code, (set = new Set()));
        set.add(id);
      }
    }
  }

  save() {
    try {
      localStorage.setItem(KEYBIND_STORAGE, JSON.stringify(this.map));
    } catch {
      // Private mode / quota — the in-memory map still drives this session.
    }
  }

  /** Physical codes currently bound to an action (order preserved). */
  keysFor(id) {
    return this.map[id] ?? [];
  }

  /** Is this code one of the ones bound to this action right now? */
  matches(id, code) {
    return (this.map[id] ?? []).includes(code);
  }

  /** Is this code bound to any control at all? (drives preventDefault.) */
  isBound(code) {
    return this._byCode.has(code);
  }

  /** Should we swallow the browser's default for this key? */
  shouldPreventDefault(code) {
    return HIJACK_CODES.has(code) && this._byCode.has(code);
  }

  /** Replace an action's whole code list. */
  set(id, codes) {
    if (!ACTION_BY_ID.has(id)) return;
    this.map[id] = [...new Set(codes.filter((c) => typeof c === "string" && c.length > 0))];
    this._reindex();
    this.save();
    this._emit();
  }

  // Bind `code` to `id`, taking it away from any OTHER action that held it. A
  // physical key doing two flight things at once is never what you want (press
  // Space to fire AND toggle autopilot?), so we steal rather than duplicate and
  // report who lost it, for the UI to narrate. Returns the ids we stole from.
  assign(id, code) {
    if (!ACTION_BY_ID.has(id) || typeof code !== "string" || !code) return [];
    const stolen = [];
    for (const other of Object.keys(this.map)) {
      if (other === id) continue;
      if (this.map[other].includes(code)) {
        this.map[other] = this.map[other].filter((c) => c !== code);
        stolen.push(other);
      }
    }
    if (!this.map[id].includes(code)) this.map[id].push(code);
    this._reindex();
    this.save();
    this._emit();
    return stolen;
  }

  // Swap oldCode → newCode IN PLACE, so re-recording a chip keeps its slot (the
  // "W" chip you rebind to "K" stays the first key, and the join-screen hint
  // reads "K/S", not "↑/S"). newCode is still stolen from any other action; if
  // oldCode isn't in the list this just appends. Returns the ids we stole from.
  replace(id, oldCode, newCode) {
    if (!ACTION_BY_ID.has(id) || typeof newCode !== "string" || !newCode) return [];
    const stolen = [];
    for (const other of Object.keys(this.map)) {
      if (other === id) continue;
      if (this.map[other].includes(newCode)) {
        this.map[other] = this.map[other].filter((c) => c !== newCode);
        stolen.push(other);
      }
    }
    const codes = this.map[id].slice();
    const idx = oldCode ? codes.indexOf(oldCode) : -1;
    if (idx >= 0) codes[idx] = newCode;
    else if (!codes.includes(newCode)) codes.push(newCode);
    this.map[id] = codes.filter((c, i) => codes.indexOf(c) === i); // de-dupe, keep order
    this._reindex();
    this.save();
    this._emit();
    return stolen;
  }

  /** Drop one code from an action (leaving it unbound if it was the last). */
  clear(id, code) {
    if (!this.map[id]) return;
    this.map[id] = this.map[id].filter((c) => c !== code);
    this._reindex();
    this.save();
    this._emit();
  }

  // Restore one action to its shipped defaults — STEALING each default code back
  // from any other action that grew to hold it, so "reset" can't resurrect the
  // one-key-two-actions state (e.g. W moved to fire, then Pitch-up reset: W must
  // leave fire, not fire alongside it).
  reset(id) {
    if (!ACTION_BY_ID.has(id)) return;
    const defs = defaultsFor(id);
    for (const other of Object.keys(this.map)) {
      if (other === id) continue;
      this.map[other] = this.map[other].filter((c) => !defs.includes(c));
    }
    this.map[id] = defs;
    this._reindex();
    this.save();
    this._emit();
  }

  /** Restore every action to its shipped defaults. */
  resetAll() {
    for (const a of ACTIONS) this.map[a.id] = a.defaults.slice();
    this._reindex();
    this.save();
    this._emit();
  }

  /** Fired after any change (save happened) — the hint text + editor repaint. */
  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit() {
    for (const fn of this._listeners) {
      try {
        fn();
      } catch {
        // A bad listener can't be allowed to wedge the keymap.
      }
    }
  }
}

export const keymap = new KeyMap();
export { KeyMap };

export function shouldPreventDefault(code) {
  return keymap.shouldPreventDefault(code);
}

// While the rebind editor is listening for a key, input.js must stand down so
// pressing (say) V to bind it doesn't also toggle mouse aim. A tiny module-level
// latch is simpler and more certain than fighting the event propagation order.
let _capturing = false;
export function setCapturing(v) {
  _capturing = !!v;
}
export function isCapturing() {
  return _capturing;
}

// ——— display labels ———
// Two label sets on purpose: the editor needs to tell L-Shift from R-Shift, but
// the terse join-screen hint just wants "Shift".
const CODE_LABELS = {
  Space: "Space", Tab: "Tab", Enter: "Enter", Escape: "Esc", Backspace: "Bksp", Delete: "Del",
  ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→",
  ShiftLeft: "LShift", ShiftRight: "RShift",
  ControlLeft: "LCtrl", ControlRight: "RCtrl",
  AltLeft: "LAlt", AltRight: "RAlt",
  MetaLeft: "LMeta", MetaRight: "RMeta",
  CapsLock: "Caps", PageUp: "PgUp", PageDown: "PgDn", Home: "Home", End: "End",
  Backquote: "`", Minus: "-", Equal: "=", BracketLeft: "[", BracketRight: "]",
  Backslash: "\\", Semicolon: ";", Quote: "'", Comma: ",", Period: ".", Slash: "/",
};

const HINT_LABELS = {
  ...CODE_LABELS,
  ShiftLeft: "Shift", ShiftRight: "Shift",
  ControlLeft: "Ctrl", ControlRight: "Ctrl",
  AltLeft: "Alt", AltRight: "Alt",
};

function labelFrom(table, code) {
  if (!code) return "—";
  if (table[code]) return table[code];
  let m;
  if ((m = /^Key([A-Z])$/.exec(code))) return m[1];
  if ((m = /^Digit(\d)$/.exec(code))) return m[1];
  if ((m = /^Numpad(\d)$/.exec(code))) return "Num" + m[1];
  if (/^F\d{1,2}$/.test(code)) return code;
  return code;
}

/** Full label for the editor chips (distinguishes left/right modifiers). */
export function codeLabel(code) {
  return labelFrom(CODE_LABELS, code);
}

/** Compact label for on-screen hints ("Shift", not "LShift"). */
export function codeHint(code) {
  return labelFrom(HINT_LABELS, code);
}

// Repaint every help token wired to a binding. index.html carries the tokens as
// <b data-kb="action"> (single action) or <b data-kb-pair="idA,idB"> (an axis
// shown as "W/S"); we fill their text from the live map so the join-screen help
// and the HUD hint bar always show the pilot's actual keys, not the shipped
// defaults. main.js calls this on boot and subscribes it to keymap.onChange.
export function repaintHints(root) {
  const doc = root ?? (typeof document !== "undefined" ? document : null);
  if (!doc) return;
  doc.querySelectorAll("[data-kb]").forEach((el) => {
    el.textContent = codeHint(keymap.keysFor(el.dataset.kb)[0]);
  });
  doc.querySelectorAll("[data-kb-pair]").forEach((el) => {
    const [a, b] = el.dataset.kbPair.split(",");
    el.textContent = `${codeHint(keymap.keysFor(a)[0])}/${codeHint(keymap.keysFor(b)[0])}`;
  });
}
